package sarka.design.skoria.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Send
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.ui.tooling.preview.Preview
import sarka.design.skoria.messaging.MessageView
import sarka.design.skoria.presentation.ConversationUiState
import sarka.design.skoria.ui.composables.DmMessageList
import sarka.design.skoria.ui.theme.RCTheme

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ConversationScreen(
    state: ConversationUiState,
    onInputChange: (String) -> Unit,
    onSend: () -> Unit,
    onLoadOlder: () -> Unit,
    onBack: () -> Unit,
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text(state.withUser, color = RCTheme.colors.primary, fontSize = 20.sp)
                        if (!state.socketConnected) {
                            // Sending still works over HTTP while the socket is
                            // down; only live delivery is affected.
                            Text(
                                text = "Reconnecting…",
                                color = RCTheme.colors.onSurfaceVariant,
                                fontSize = 12.sp,
                            )
                        }
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                    }
                }
            )
        },
        bottomBar = {
            Column(
                modifier = Modifier
                    .imePadding()
                    .windowInsetsPadding(WindowInsets.navigationBars)
            ) {
                if (state.error != null) {
                    Text(
                        text = state.error,
                        color = RCTheme.colors.error,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(horizontal = 12.dp),
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TextField(
                        value = state.input,
                        onValueChange = onInputChange,
                        enabled = !state.isSending,
                        modifier = Modifier.weight(1f).onPreviewKeyEvent { e ->
                            val isEnter = e.key == Key.Enter || e.key == Key.NumPadEnter
                            if (e.type == KeyEventType.KeyDown && isEnter) {
                                if (state.input.isNotBlank() && !state.isSending) onSend()
                                true
                            } else {
                                false
                            }
                        },
                        placeholder = { Text("Message ${state.withUser}") },
                        singleLine = true,
                    )

                    IconButton(
                        onClick = onSend,
                        enabled = state.input.isNotBlank() && !state.isSending,
                        colors = IconButtonDefaults.iconButtonColors(
                            contentColor = RCTheme.colors.onSurface,
                            disabledContentColor = RCTheme.colors.onSurface.copy(alpha = 0.38f)
                        )
                    ) {
                        Icon(
                            modifier = Modifier.size(40.dp),
                            imageVector = Icons.AutoMirrored.Filled.Send,
                            contentDescription = "Send",
                        )
                    }
                }
            }
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            when {
                state.isLoading && state.messages.isEmpty() ->
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }

                state.messages.isEmpty() ->
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Text(
                            text = "No messages yet. Say something.",
                            color = RCTheme.colors.onSurfaceVariant,
                            fontSize = 14.sp,
                        )
                    }

                else ->
                    Column(modifier = Modifier.fillMaxSize()) {
                        if (state.hasMore) {
                            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                                if (state.isLoadingOlder) {
                                    CircularProgressIndicator(modifier = Modifier.size(20.dp))
                                } else {
                                    TextButton(onClick = onLoadOlder) {
                                        Text("Load earlier messages", fontSize = 13.sp)
                                    }
                                }
                            }
                        }

                        DmMessageList(messages = state.messages)
                    }
            }
        }
    }
}

@Composable
@Preview
private fun ConversationScreenPreview() {
    RCTheme {
        ConversationScreen(
            state = ConversationUiState(
                withUser = "alice",
                messages = listOf(
                    MessageView("1", fromMe = false, text = "Hey", sentAt = 1L),
                    MessageView("2", fromMe = true, text = "Hello", sentAt = 2L, delivered = true, read = true),
                    MessageView("3", fromMe = true, text = "Hello again", sentAt = 2L, delivered = true, read = false),
                    MessageView("4", fromMe = true, text = "How are you................................................? sdasfaf", sentAt = 3L),
                ),
            ),
            onInputChange = {},
            onSend = {},
            onLoadOlder = {},
            onBack = {},
        )
    }
}
